import { StatusTileValueMapEntry, StatusTileWidgetConfig } from "../types";

/** The fields object a `status_tile` reads from: a CUSTOM_OBJECT's JSON `value`, or a plain record. */
export type TileFields = Record<string, unknown>;

export interface ResolvedStatusTile {
    /** The stringified value of `config.field` (empty string when absent). */
    stateKey: string;
    /** The appearance to render, or undefined when neither a value_map entry nor a default applies. */
    entry?: StatusTileValueMapEntry;
    /** True when the staleness/heartbeat override produced the entry. */
    stale: boolean;
}

/**
 * Resolve which appearance a `status_tile` should show — a PURE function of the
 * widget config, the record's fields, and the current time (ms), so the
 * value→appearance mapping and the staleness/dead-heartbeat override are
 * unit-testable without rendering.
 *
 * Order: `value_map[value]` → `default`, then a staleness override (when the
 * record's freshness timestamp + grace has passed and the current value is not
 * in `skip_when_values`).
 */
export const resolveStatusTile = (
    config: StatusTileWidgetConfig | undefined,
    fields: TileFields | null | undefined,
    nowMs: number
): ResolvedStatusTile => {
    if (!config || !fields) {
        return { stateKey: "", entry: undefined, stale: false };
    }
    const rawState = config.field ? fields[config.field] : undefined;
    const stateKey = rawState == null ? "" : String(rawState);

    let entry: StatusTileValueMapEntry | undefined =
        (config.value_map && config.value_map[stateKey]) || config.default;
    let stale = false;

    const staleCfg = config.stale;
    if (staleCfg?.timestamp_field) {
        const skip = staleCfg.skip_when_values ?? [];
        if (!skip.includes(stateKey)) {
            const tsRaw = fields[staleCfg.timestamp_field];
            if (tsRaw != null) {
                const freshUntil = new Date(String(tsRaw)).getTime();
                const grace = (staleCfg.grace_seconds ?? 0) * 1000;
                if (!Number.isNaN(freshUntil) && nowMs > freshUntil + grace) {
                    entry = {
                        color: staleCfg.color,
                        label: staleCfg.label,
                        detail: staleCfg.detail,
                    };
                    stale = true;
                }
            }
        }
    }

    return { stateKey, entry, stale };
};

/** Format a value read from a record for a status_tile detail sub-line. */
export const formatStatusTileDetail = (raw: unknown, format?: string): string => {
    if (raw == null) {
        return "";
    }
    if (format === "datetime") {
        const d = new Date(String(raw));
        if (Number.isNaN(d.getTime())) {
            return String(raw);
        }
        return `${d.toLocaleString()} (approx)`;
    }
    return String(raw);
};
